import {
  type IPracticeMigration,
  type ITranslationMap,
} from '@principle-theorem/practice-migrations/interfaces';
import { type WithPrimaryKey, type WithRef } from '@principle-theorem/shared';
import { first } from 'lodash';
import { ItemCodeNoteType } from '../../../../mappings/item-codes-to-notes-xlsx';
import {
  runDatabaseRequest,
  type IQueryResult,
} from '../../../../source/connection';
import { type IOasisPatientNote } from '../../../source/entities/patient-notes';

interface INoteCategoryPresenceRow {
  hasNoteCategories: boolean;
}

const appointmentLogNotePrefix = 'APPS.';
const excludedNoteTypeDestinations = [
  ItemCodeNoteType.SterilisationRecord,
  ItemCodeNoteType.Omit,
];
const noteCategoryPresenceQuery = `SELECT EXISTS (SELECT 1 FROM SYTBLENT WHERE SKEY LIKE 'CLNCATGSE%') AS "hasNoteCategories"`;

/**
 * Older OASiS exports have no note categories (no CLNCATGSE rows and no
 * PTPATNTS.CATEGORY column), so notes of a non-zero note type arrive without a
 * category. Those notes become clinical notes, except appointment logs that
 * OASiS writes itself ("APPS." cancellations, late arrivals, no-shows) and
 * note types the operator mapped to sterilisation records or to Omit.
 */
export class OasisUncategorisedNotes {
  static async exportHasNoteCategories(
    migration: WithRef<IPracticeMigration>
  ): Promise<boolean> {
    const result = await runDatabaseRequest<
      IQueryResult<INoteCategoryPresenceRow>
    >({
      migration,
      query: noteCategoryPresenceQuery,
    });
    return first(result.rows)?.hasNoteCategories === true;
  }

  static isClinicalNote(
    note: Pick<IOasisPatientNote, 'note' | 'noteType'>,
    noteTypes: WithPrimaryKey<ITranslationMap<object, ItemCodeNoteType>>[]
  ): boolean {
    const isAppointmentLog = note.note
      .trimStart()
      .toUpperCase()
      .startsWith(appointmentLogNotePrefix);
    if (isAppointmentLog || note.noteType === undefined) {
      return false;
    }
    const noteTypeId = note.noteType.toString();
    const destination = noteTypes.find(
      (noteType) => noteType.sourceIdentifier === noteTypeId
    )?.destinationValue?.value;
    return !destination || !excludedNoteTypeDestinations.includes(destination);
  }
}
